import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { fetchProfile, resetProfileCache } from "@/lib/profile-cache";

/**
 * Appearance: colour mode, typeface, corners.
 *
 * One store, rolled by hand rather than next-themes, because next-themes
 * knows only light/dark and a second store for the other axes would be two
 * sources of truth. Every axis is an attribute on <html>; index.css does the
 * rest.
 *
 * The attributes exist only while a surface is active — AppShell declares
 * one — so the landing, docs, pricing and auth pages never see them. That is
 * the whole of "app only".
 *
 * The browser copy is a cache of the account's copy. Which side wins on
 * sign-in is decided by whether either is still at its defaults (see the
 * sync effect); only when both were customised does the account win.
 */

export const STORAGE_KEY = "nextopp:appearance";

export const DEFAULTS = { mode: "light", font: "daybreak", radius: "sharp" };

export const MODES = [
  { id: "light", label: "Paper" },
  { id: "dark", label: "Night" },
  { id: "system", label: "System" },
];

export const FONTS = [
  { id: "daybreak", label: "Daybreak", note: "Cabinet Grotesk and Satoshi. The default." },
  { id: "geometric", label: "Geometric", note: "Outfit throughout." },
  { id: "neutral", label: "Neutral", note: "Inter throughout." },
  { id: "system", label: "System", note: "No webfont. Fastest to render." },
];

export const RADII = [
  { id: "sharp", label: "Sharp" },
  { id: "cut", label: "Cut" },
  { id: "soft", label: "Soft" },
  { id: "round", label: "Round" },
];

/** What `rounded-sm` renders at, for the swatches on the settings page. */
export const RADIUS_PX = { sharp: 0, cut: 2, soft: 6, round: 12 };

const VALID = {
  mode: MODES.map((m) => m.id),
  font: FONTS.map((f) => f.id),
  radius: RADII.map((r) => r.id),
};

/** Web fonts beyond the default pairing are fetched only when chosen. */
const FONT_HREF = {
  geometric: "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800&display=swap",
  neutral: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;800&display=swap",
};

export function loadFontFor(font) {
  const href = FONT_HREF[font];
  if (!href || typeof document === "undefined") return;
  if (document.querySelector(`link[data-font-for="${font}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.fontFor = font;
  document.head.appendChild(link);
}

/** Holds every axis to its list; anything else becomes the default. */
export function sanitize(p) {
  const out = { ...DEFAULTS };
  for (const k of Object.keys(DEFAULTS)) {
    if (p && VALID[k].includes(p[k])) out[k] = p[k];
  }
  return out;
}

export const isDefaults = (p) => Object.keys(DEFAULTS).every((k) => (p?.[k] ?? DEFAULTS[k]) === DEFAULTS[k]);

export function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? sanitize(JSON.parse(raw)) : { ...DEFAULTS };
  } catch {
    // A corrupt or unavailable store degrades to the defaults, not a crash.
    return { ...DEFAULTS };
  }
}

export function resolveMode(mode) {
  if (mode !== "system") return mode;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
}

/** The only writer to <html>. Inactive means every trace is removed. */
export function applyAppearance(prefs, active) {
  const el = document.documentElement;
  if (!active) {
    el.classList.remove("dark");
    delete el.dataset.font;
    delete el.dataset.radius;
    el.style.removeProperty("color-scheme");
    return "light";
  }
  const resolved = resolveMode(prefs.mode);
  el.classList.toggle("dark", resolved === "dark");
  el.dataset.font = prefs.font;
  el.dataset.radius = prefs.radius;
  el.style.colorScheme = resolved;
  return resolved;
}

const noop = () => {};
const AppearanceContext = createContext({
  prefs: DEFAULTS, set: noop, reset: noop, resolvedMode: "light", active: false, setActive: noop,
});

export function AppearanceProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user?.user_id || user?.email || null;
  const [prefs, setPrefs] = useState(readStored);
  const [active, setActive] = useState(false);
  const [resolvedMode, setResolvedMode] = useState("light");

  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  // Which account this browser's copy has been reconciled with. Saves are
  // gated on it so a sign-in can never write the browser copy over an
  // account value that has not been read yet.
  const syncedFor = useRef(null);
  const fetchingFor = useRef(null);
  const skipNextSave = useRef(false);

  useEffect(() => {
    setResolvedMode(applyAppearance(prefs, active));
    if (active) loadFontFor(prefs.font);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* a preference that cannot persist is still a preference for this page */
    }
  }, [prefs, active]);

  // The provider is the writer, so the provider going away takes every
  // attribute with it — a surface unmounting at the same instant cannot
  // reach a provider that is no longer rendering.
  useEffect(() => () => applyAppearance(DEFAULTS, false), []);

  // Follow the OS only while the user has actually chosen "system".
  useEffect(() => {
    if (!active || prefs.mode !== "system" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolvedMode(applyAppearance(prefs, true));
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [prefs, active]);

  // Reconcile with the account once per sign-in.
  useEffect(() => {
    if (!userId) {
      syncedFor.current = null;
      fetchingFor.current = null;
      return;
    }
    if (fetchingFor.current === userId) return;
    fetchingFor.current = userId;
    fetchProfile().then((p) => {
      if (fetchingFor.current !== userId) return;
      const remote = p?.appearance;
      if (remote && !isDefaults(remote)) {
        const next = sanitize(remote);
        // Only skip the next save if a save is actually coming: an identical
        // value bails out of the state update and the save effect never runs,
        // which would leave the flag armed for the user's first real change.
        if (JSON.stringify(next) !== JSON.stringify(prefsRef.current)) {
          skipNextSave.current = true;
          setPrefs(next);
        }
      } else if (!isDefaults(prefsRef.current)) {
        api.put("/profile/appearance", prefsRef.current).then(() => resetProfileCache()).catch(() => {});
      }
      syncedFor.current = userId;
    });
  }, [userId]);

  // Persist later changes, debounced: stepping through the corner chips
  // should not put four writes on the wire.
  useEffect(() => {
    if (!userId || syncedFor.current !== userId) return undefined;
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      api.put("/profile/appearance", prefs).then(() => resetProfileCache()).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [prefs, userId]);

  const set = useCallback((patch) => setPrefs((p) => sanitize({ ...p, ...patch })), []);
  const reset = useCallback(() => setPrefs({ ...DEFAULTS }), []);

  const value = useMemo(
    () => ({ prefs, set, reset, resolvedMode, active, setActive }),
    [prefs, set, reset, resolvedMode, active],
  );
  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export const useAppearance = () => useContext(AppearanceContext);

/** Declared by AppShell: while mounted, the attributes are on <html>. */
export function useAppearanceSurface() {
  const { setActive } = useContext(AppearanceContext);
  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);
}
